import * as React from 'react';

import styled, { css } from 'styled-components';

import Flex from '@components/layout/flex';
import Avatar from '@components/basics/avatar';
import Typography from '@components/basics/typography';
import UnselectedIcon from '@components/basics/icons/unselected';
import CircleCheckFilled from '@components/basics/icons/circle-check-filled';

import SimpleArtist from '@common/types/artist/simple-artist';
import { Translate } from 'react-localize-redux';
import AppState from '@webapp/store/state';
import { connect } from 'react-redux';

interface ArtistListItemProps {
  className?: string;
  onHeader?: boolean;
  noHover?: boolean;
  isSelected?: boolean;
  noTruncate?: boolean;
  noIcons?: boolean;
  hasBigAvatar?: boolean;
  artist: SimpleArtist;
  labelName?: string;
  multipleArtists?: SimpleArtist[];
  isMobile?: boolean;
  onClick?: () => void;
}

const mapStateToProps = (state: AppState) => ({
  isMobile: state.ui.mobile,
});

export const ArtistListItem: React.FC<ArtistListItemProps> = (props) => {
  return (
    <Flex
      onClick={props.onClick}
      alignItems="center"
      className={props.className}
    >
      {props.multipleArtists ? (
        <MultipleAvatars
          className="multiple-avatars"
          artistsCount={props.multipleArtists.length}
        >
          {props.multipleArtists.slice(0, 5).map((artist, i) => (
            <Avatar
              key={i}
              className="avatar"
              alt={artist.name}
              src={artist.avatar}
              size="32"
            />
          ))}
        </MultipleAvatars>
      ) : (
        <Avatar
          className="avatar"
          alt={props.artist.name}
          src={props.artist.avatar}
          size={props.isMobile && props.onHeader ? '40' : undefined}
        />
      )}
      <div className="artist-info">
        <Typography className="artist-name">
          {props.multipleArtists ? (
            <Translate id="DASHBOARD.NEW_DASHBOARD.MULTIPLE_ARTISTS" />
          ) : props.artist?.name?.length > 10 &&
            props.isMobile &&
            !props.noTruncate ? (
            `${props.artist.name.slice(0, 10)}...`
          ) : (
            props.artist.name
          )}
        </Typography>
      </div>
      {!props.onHeader && !props.noIcons ? (
        props.isSelected ? (
          <CircleCheckFilled className="icon" />
        ) : (
          <UnselectedIcon className="icon" />
        )
      ) : null}
    </Flex>
  );
};

export default connect(mapStateToProps)(styled(ArtistListItem)`
  cursor: ${(props) => (props.noHover ? 'auto' : 'pointer')};
  user-select: none;
  background-color: transparent;
  position: relative;
  padding-left: 16px;
  transition: background-color
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  @media ${({ theme }) => theme.device.tablet} {
    width: auto;

    ${(props) =>
      !props.noHover
        ? css`
            padding-right: 42px;
          `
        : null}
  }

  ${(props) =>
    props.onHeader
      ? css`
          width: 40px;
          padding: 0;
          border-radius: 90px;
          background-color: ${({ theme }) => theme.colors.midGray};

          &:hover {
            background-color: ${({ theme }) =>
              theme.colors.iconBackgroundHover};
          }

          @media ${({ theme }) => theme.device.tablet} {
            padding: 0;
            height: 40px;
            width: auto;
          }

          @media ${({ theme }) => theme.device.laptopS} {
            padding: 5px 10px 5px 5px;
          }

          .artist-info {
            margin-left: 10px;
          }

          .artist-name {
            display: none;

            @media ${({ theme }) => theme.device.laptopS} {
              display: block;
            }
          }
        `
      : css`
          .artist-info {
            margin-left: 16px;
          }
        `}

  .multiple-avatars {
    flex-shrink: 0;
    position: relative;
    .avatar {
      width: 32px;
      position: absolute;
      top: 50%;
      left: 0;
      transform: translate(0, -50%);

      &:nth-child(2) {
        left: 16px;
      }
      &:nth-child(3) {
        left: 32px;
      }
      &:nth-child(4) {
        left: 48px;
      }
      &:nth-child(5) {
        left: 56px;
      }
    }
  }

  .avatar {
    flex-shrink: 0;

    ${(props) =>
      props.onHeader
        ? css`
            width: 40px;
            height: 40px;
          `
        : css`
            width: 32px;
            height: 32px;
          `}

    @media ${({ theme }) => theme.device.laptopS} {
      ${(props) =>
        props.onHeader
          ? css`
              width: 32px;
              height: 32px;
            `
          : css`
              width: 32px;
              height: 32px;
            `}

      ${(props) =>
        props.hasBigAvatar
          ? css`
              width: 32px;
              height: 32px;
            `
          : null}
    }
  }

  .artist-name {
    font-weight: 700;
    color: ${({ theme }) => theme.colors.primary};
  }

  .label-name {
    margin-top: 2px;
    font-weight: 400;
    color: ${({ theme }) => theme.colors.tertiary};
  }

  .icon {
    width: 24px;
    height: 24px;
    position: absolute;
    top: 50%;
    right: 0;
    transform: translate(0, -50%);
  }
`);

const MultipleAvatars = styled.div<{ artistsCount?: number }>`
  ${({ artistsCount }) =>
    artistsCount &&
    css`
      width: calc((${artistsCount} + 1) * 16px);
    `}
`;
